import {
  Flex,
  Grid,
  GridItem,
  Tag,
  TagLabel,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { DetailField } from "../../../components/detailField/DetailField";
import React from "react";
import { formatTextDisplay } from "../../../utils/displayGetters";
import { SegmentBundleContextHandlerType } from "../../../graphql/generated";

type SegmentContextDisplayProps = {
  bundleContext: SegmentBundleContextHandlerType;
};

type InfoTagProps = {
  label: string | number;
  bg?: string;
};

const NameTag = ({ label, bg = "blueInputHover" }: InfoTagProps) => (
  <Tooltip label={label} hasArrow placement="top" openDelay={300}>
    <Tag
      size="md"
      backgroundColor={bg}
      borderRadius="full"
      whiteSpace="normal"
      wordBreak="break-word"
      px={3}
    >
      <TagLabel
        marginTop={1}
        marginBottom={1}
        display="inline"
        whiteSpace="normal"
        wordBreak="break-word"
      >
        {label}
      </TagLabel>
    </Tag>
  </Tooltip>
);

export const SegmentContextDisplay = ({
  bundleContext,
}: SegmentContextDisplayProps) => {
  return (
    <>
      <Flex
        flexWrap="wrap"
        mt={8}
        direction="column"
        alignItems="flex-start"
        width={"100%"}
        p={4}
        mb={5}
        borderWidth={1}
        borderRadius="md"
        backgroundColor="white"
      >
        <Text fontWeight={"bold"}>{bundleContext.campaign?.name}</Text>
        <Grid
          templateColumns="auto auto 1fr"
          gap={4}
          mb={4}
          mt={3}
          alignItems="flex-start"
          maxWidth="100%"
        >
          <GridItem>
            <Text
              fontWeight="semibold"
              fontSize="sm"
              color="primary.darker"
              mb={2}
            >
              Content
            </Text>
          </GridItem>

          <GridItem>
            <Text
              fontWeight="semibold"
              fontSize="sm"
              color="primary.darker"
              mb={2}
            >
              Id
            </Text>
          </GridItem>

          <GridItem>
            <Text
              fontWeight="semibold"
              fontSize="sm"
              color="primary.darker"
              mb={2}
            >
              Name
            </Text>
          </GridItem>
          {bundleContext.bundle.segment && (
            <>
              <GridItem>
                <Text size="md">Segment</Text>
              </GridItem>
              <GridItem>
                <Tag
                  size="md"
                  backgroundColor="blueInputHover"
                  borderRadius="full"
                >
                  <TagLabel>{bundleContext.bundle.segment.segmentId}</TagLabel>
                </Tag>
              </GridItem>

              <GridItem minW={0}>
                <NameTag label={bundleContext.bundle.segment.name} />
              </GridItem>
            </>
          )}

          {bundleContext.bundle.deal && (
            <>
              <GridItem>
                <Text size="md">Deal</Text>
              </GridItem>
              <GridItem>
                <Tag
                  size="md"
                  backgroundColor="blueInputHover"
                  borderRadius="full"
                >
                  <TagLabel>{bundleContext.bundle.deal.dealId}</TagLabel>
                </Tag>
              </GridItem>

              <GridItem minW={0}>
                <NameTag label={bundleContext.bundle.deal.name} />
              </GridItem>
            </>
          )}
        </Grid>
        <Flex
          mt={3}
          width={"100%"}
          direction={"row"}
          justifyContent={"space-between"}
          borderTop={"1px"}
          borderTopColor={"#EAF0F2"}
        >
          <DetailField
            mt={4}
            label="Tactic Level:"
            value={formatTextDisplay(bundleContext.lineType)}
            variant="flex"
          />
          <Grid
            mt={4}
            templateColumns="flex"
            gap={2}
            mb={4}
            alignItems={"flex-start"}
          >
            <GridItem colSpan={10}>
              <Text
                color={"primary.darker"}
                fontWeight={"semibold"}
                fontSize={"sm"}
                mr={2}
              >
                Tactic Ids:
              </Text>
            </GridItem>
            {bundleContext.relevantIds.map(
              (lineItemId: string, index: number) => (
                <GridItem
                  colSpan={5}
                  key={index}
                  alignContent={"center"}
                  justifyContent={"center"}
                >
                  <Tag size={"md"} colorScheme="teal" borderRadius={"full"}>
                    <TagLabel>{lineItemId}</TagLabel>
                  </Tag>
                </GridItem>
              )
            )}
          </Grid>
          <Grid
            mt={4}
            templateColumns="flex"
            gap={2}
            mb={4}
            alignItems={"flex-start"}
          >
            <GridItem colSpan={10}>
              <Text
                color={"primary.darker"}
                fontWeight={"semibold"}
                fontSize={"sm"}
                mr={2}
              >
                Non-Illuma Tactic Ids:
              </Text>
            </GridItem>
            {bundleContext.nonIllumaIds?.map(
              (nonIllumaIds: string, index: number) => (
                <GridItem
                  colSpan={10}
                  key={index}
                  alignContent={"center"}
                  justifyContent={"center"}
                >
                  <Tag size={"md"} colorScheme="teal" borderRadius={"full"}>
                    <TagLabel>{nonIllumaIds}</TagLabel>
                  </Tag>
                </GridItem>
              )
            )}
          </Grid>
        </Flex>
      </Flex>
    </>
  );
};
